import { useRef, useState } from 'react';
import { uploadToCloudinary } from '@/lib/cloudinary';

interface MultiImageUploaderProps {
  images: string[];
  mainImageIndex: number;
  onChange: (images: string[], mainImageIndex: number) => void;
  maxImages?: number;
}

const MultiImageUploader = ({ images, mainImageIndex, onChange, maxImages = 3 }: MultiImageUploaderProps) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [loadingSlots, setLoadingSlots] = useState<Record<number, boolean>>({});
  const [progressSlots, setProgressSlots] = useState<Record<number, number>>({});
  const [errors, setErrors] = useState<string[]>([]);

  const handleFile = async (file: File, slotIdx: number) => {
    if (!file.type.startsWith('image/')) {
      const errs = [...errors];
      errs[slotIdx] = 'Lütfen bir görsel dosyası seçin.';
      setErrors(errs);
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      const errs = [...errors];
      errs[slotIdx] = 'Dosya boyutu 15MB\'dan büyük olamaz.';
      setErrors(errs);
      return;
    }
    const errs = [...errors];
    errs[slotIdx] = '';
    setErrors(errs);
    setLoadingSlots(prev => ({ ...prev, [slotIdx]: true }));
    setProgressSlots(prev => ({ ...prev, [slotIdx]: 10 }));

    const progressInterval = setInterval(() => {
      setProgressSlots(prev => ({
        ...prev,
        [slotIdx]: Math.min((prev[slotIdx] ?? 10) + 15, 85),
      }));
    }, 300);

    try {
      const result = await uploadToCloudinary(file);
      clearInterval(progressInterval);
      setProgressSlots(prev => ({ ...prev, [slotIdx]: 100 }));

      const newImages = [...images];
      newImages[slotIdx] = result.secure_url;
      const filledImages = newImages.filter(Boolean);
      const newMain = filledImages.length === 1 ? newImages.findIndex(Boolean) : mainImageIndex;
      onChange(newImages, newMain);
    } catch (err) {
      clearInterval(progressInterval);
      const errs2 = [...errors];
      errs2[slotIdx] = err instanceof Error ? err.message : 'Yükleme hatası oluştu.';
      setErrors(errs2);
    } finally {
      setLoadingSlots(prev => ({ ...prev, [slotIdx]: false }));
      setTimeout(() => setProgressSlots(prev => ({ ...prev, [slotIdx]: 0 })), 500);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, slotIdx: number) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file, slotIdx);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent, slotIdx: number) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file, slotIdx);
  };

  const removeImage = (slotIdx: number) => {
    const newImages = [...images];
    newImages[slotIdx] = '';
    let newMain = mainImageIndex;
    if (mainImageIndex === slotIdx) {
      newMain = newImages.findIndex((img, i) => i !== slotIdx && img);
      if (newMain === -1) newMain = 0;
    }
    onChange(newImages, newMain);
  };

  const setMain = (slotIdx: number) => {
    if (!images[slotIdx]) return;
    onChange([...images], slotIdx);
  };

  const slots = Array.from({ length: maxImages }, (_, i) => i);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xs font-semibold text-slate-700">Ürün Fotoğrafları</span>
        <span className="text-xs text-slate-400">(En fazla {maxImages} fotoğraf)</span>
        <span className="text-xs text-green-600 flex items-center gap-1 ml-auto">
          <i className="ri-check-double-line"></i> Cloudinary bulut depolama
        </span>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {slots.map((slotIdx) => {
          const imgVal = images[slotIdx] ?? '';
          const isMain = mainImageIndex === slotIdx && !!imgVal;
          const isLoading = loadingSlots[slotIdx] ?? false;
          const progress = progressSlots[slotIdx] ?? 0;

          return (
            <div key={slotIdx} className="flex flex-col gap-1.5">
              <input
                ref={el => { inputRefs.current[slotIdx] = el; }}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={e => handleChange(e, slotIdx)}
              />

              {imgVal ? (
                <div
                  className={`relative rounded-xl overflow-hidden bg-slate-100 border-2 transition-all duration-200 cursor-pointer ${
                    isMain ? 'border-orange-500' : 'border-slate-200 hover:border-orange-300'
                  }`}
                  style={{ height: 110 }}
                  onClick={() => setMain(slotIdx)}
                  title="Ana resim olarak seç"
                >
                  <img src={imgVal} alt={`Foto ${slotIdx + 1}`} className="w-full h-full object-cover object-top" />

                  {isMain && (
                    <div className="absolute top-1.5 left-1.5 bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full font-semibold flex items-center gap-0.5">
                      <i className="ri-star-fill text-xs"></i> Ana
                    </div>
                  )}

                  {!isMain && (
                    <div className="absolute top-1.5 left-1.5 bg-black/40 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full font-bold">
                      {slotIdx + 1}
                    </div>
                  )}

                  <div className="absolute inset-0 bg-black/0 hover:bg-black/50 transition-all duration-200 flex items-center justify-center opacity-0 hover:opacity-100 gap-1.5">
                    <button
                      type="button"
                      onClick={e => { e.stopPropagation(); inputRefs.current[slotIdx]?.click(); }}
                      className="bg-white text-slate-900 p-1.5 rounded-lg text-xs cursor-pointer"
                      title="Değiştir"
                    >
                      <i className="ri-refresh-line"></i>
                    </button>
                    <button
                      type="button"
                      onClick={e => { e.stopPropagation(); removeImage(slotIdx); }}
                      className="bg-red-500 text-white p-1.5 rounded-lg text-xs cursor-pointer"
                      title="Kaldır"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onDrop={e => handleDrop(e, slotIdx)}
                  onDragOver={e => e.preventDefault()}
                  onClick={() => !isLoading && inputRefs.current[slotIdx]?.click()}
                  className={`border-2 border-dashed rounded-xl text-center transition-all duration-200 flex flex-col items-center justify-center gap-1 ${
                    isLoading
                      ? 'border-orange-400 bg-orange-50/30 cursor-wait'
                      : 'border-slate-300 hover:border-orange-400 cursor-pointer bg-slate-50 hover:bg-orange-50/30'
                  }`}
                  style={{ height: 110 }}
                >
                  {isLoading ? (
                    <div className="flex flex-col items-center gap-1.5 w-full px-3">
                      <i className="ri-upload-cloud-2-line text-xl text-orange-500 animate-pulse"></i>
                      <div className="w-full bg-slate-200 rounded-full h-1 overflow-hidden">
                        <div
                          className="bg-orange-500 h-1 rounded-full transition-all duration-300"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <span className="text-orange-500 text-xs">Yükleniyor...</span>
                    </div>
                  ) : (
                    <>
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-orange-100">
                        <i className="ri-upload-cloud-2-line text-base text-orange-500"></i>
                      </div>
                      <span className="text-slate-500 text-xs font-medium">Foto {slotIdx + 1}</span>
                      <span className="text-slate-400 text-xs">Tıkla veya sürükle</span>
                    </>
                  )}
                </div>
              )}

              {imgVal && !isMain && (
                <button
                  type="button"
                  onClick={() => setMain(slotIdx)}
                  className="text-xs text-slate-500 hover:text-orange-500 transition-colors duration-150 flex items-center justify-center gap-1 cursor-pointer py-0.5"
                >
                  <i className="ri-star-line text-xs"></i> Ana Resim Yap
                </button>
              )}
              {imgVal && isMain && (
                <div className="text-xs text-orange-500 flex items-center justify-center gap-1 py-0.5 font-semibold">
                  <i className="ri-star-fill text-xs"></i> Ana Resim
                </div>
              )}

              {errors[slotIdx] && (
                <p className="text-red-500 text-xs flex items-center gap-1">
                  <i className="ri-error-warning-line"></i> {errors[slotIdx]}
                </p>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-slate-400 text-xs flex items-center gap-1">
        <i className="ri-information-line"></i>
        Fotoğraflar Cloudinary bulut sunucusuna yüklenir — herkes her cihazdan görebilir.
      </p>
    </div>
  );
};

export default MultiImageUploader;
